// src/components/chart/CourseProgressChart.jsx
import React, { useState } from 'react';
import {
    BarChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import GraphModal from '../graphModal/GraphModal';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterBadge from '../common/FilterBadge';
import Skeleton from "@mui/material/Skeleton";
import { monthNames } from '../../enum.js';
import './courseProgressChart.scss';

const CourseProgressChart = ({ title = "Course Status", data = [], loading, appliedFilters = [] }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const hasFilters = appliedFilters.length > 0 && !loading;

    // ✅ Safely transform API data for the chart
    const chartData = data.map(item => {
        const [year, mon] = item.month.split('-');
        const monthName = `${monthNames[parseInt(mon, 10) - 1]} ${year}`;
        const achieved = parseFloat(item.month_status?.total_achieve_unit ?? 0);
        const targeted = parseFloat(item.month_status?.total_targeted_unit ?? 0);
        return {
            month: monthName,
            achieved,
            remaining: Math.max(targeted - achieved, 0),
            percentage: parseFloat(item.month_status?.completion_percentage ?? 0)
        };
    });

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const { achieved, remaining, percentage } = payload[0].payload;
            return (
                <div className="custom-tooltip">
                    <p className="label">{label}</p>
                    <p>Achieved: {achieved} units</p>
                    <p>Remaining: {remaining} units</p>
                    <p>Targeted: {achieved + remaining} units</p>
                    <p>Completion: {percentage}%</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div
            className="courseProgressChartContainer"
            onMouseEnter={() => setShowFilters(true)}
            onMouseLeave={() => setShowFilters(false)}
        >
            <div className="chart-header">
                <h2 className="chartTitle">{title}</h2>
                {hasFilters && <FilterListIcon fontSize="small" className="filter-indicator" />}
            </div>

            {hasFilters && showFilters && (
                <div className="filter-tooltip top-left">
                    <div className="filter-header">Applied Filters:</div>
                    <div className="filter-badges">
                        {appliedFilters.map((filter, idx) => (
                            <FilterBadge key={idx} label={filter.label} value={filter.value} />
                        ))}
                    </div>
                </div>
            )}

            {loading ? (
                <Skeleton variant="rectangular" width="100%" height={400} animation="wave" />
            ) : chartData.length > 0 ? (
                <>
                    <div className="chart-wrapper" onClick={() => setIsModalOpen(true)}>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis yAxisId="units" domain={[0, 'auto']} />
                                <YAxis yAxisId="percentage" orientation="right" domain={[0, 100]} unit="%" />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar yAxisId="units" dataKey="achieved" stackId="a" fill="#00C49F" name="Achieved" />
                                <Bar yAxisId="units" dataKey="remaining" stackId="a" fill="#FF8042" name="Remaining" />
                                <Line yAxisId="percentage" type="monotone" dataKey="percentage" stroke="#0088FE" name="Completion %" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Modal View */}
                    <GraphModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        title={title}
                        data={chartData}
                    />
                </>
            ) : (
                <p className="no-data">No data available</p>
            )}
        </div>
    );
};

export default CourseProgressChart;
